import { Key, useState } from 'react';
import { AppSidebarMenuItem, SidebarLinkLegacy } from './AppSidebarMenuItem';
import styled from 'styled-components';
import { Badge } from '@material-ui/core';
import { Color } from '@superdispatch/ui';

const SidebarMenuContainer = styled.ul`
  padding: 0;
  width: 100%;
  list-style: none;
  margin: 0;
`;
const StyledBadge = styled(Badge)<{ $highlighted: boolean }>`
  & .MuiBadge-badge {
    border: 2px solid
      ${(props) => (props.$highlighted ? Color.Dark400 : Color.Dark500)};
    top: 2px;
  }

  & .MuiBadge-dot {
    margin-top: 2px;
    margin-right: 2px;
    height: 16px;
    width: 16px;
    border-radius: 50%;
  }
`;
const Gutter = styled.div<{ $hidden?: boolean }>`
  height: 20px;
  display: ${(props) => (props.$hidden ? 'none' : 'block')};
`;
const StyledLinkLegacyIcon = styled.a`
  width: 20px;
  height: 20px;
  margin-bottom: 4px;

  path {
    fill: ${Color.Silver500};
  }
`;

interface SidebarMenuProps {
  links: SidebarLinkLegacy[];
  disableGutter?: boolean;
  overflowedLinkKeys: Set<Key>;
  itemRefs: React.MutableRefObject<Map<Key, HTMLElement>>;
  gutterId?: Key;
}

export function AppSidebarMenuGroup({
  links,
  disableGutter,
  overflowedLinkKeys,
  itemRefs,
  gutterId,
}: SidebarMenuProps) {
  const [isHovered, setIsHovered] = useState(false);
  const visibleLinks = links.filter((link) => !link.hidden);

  return (
    <SidebarMenuContainer
      data-intercom-target="siebar-menu-section"
      data-disablegutter={!!disableGutter}
      onMouseOver={() => {
        setIsHovered(true);
      }}
      onMouseOut={() => {
        setIsHovered(false);
      }}
    >
      {visibleLinks.map((link) => {
        const isOverflowed = overflowedLinkKeys.has(link.key);
        return (
          <AppSidebarMenuItem
            key={link.key}
            link={link}
            dataIntercomTarget={link.dataIntercomTarget}
            hidden={isOverflowed}
            itemRef={(el) => {
              if (el) {
                itemRefs.current.set(link.key, el);
              } else {
                itemRefs.current.delete(link.key);
              }
            }}
          >
            <StyledBadge
              badgeContent={link.badgeContent}
              color="error"
              overlap="rectangular"
              max={999}
              $highlighted={isHovered}
              variant={link.badgeType}
              invisible={!link.badgeContent}
            >
              <StyledLinkLegacyIcon as={link.Icon} />
            </StyledBadge>
            {link.label}
          </AppSidebarMenuItem>
        );
      })}
      {!disableGutter && gutterId != null ? (
        <Gutter
          ref={(el) => {
            if (el) {
              itemRefs.current.set(gutterId, el);
            } else {
              itemRefs.current.delete(gutterId);
            }
          }}
          $hidden={overflowedLinkKeys.has(gutterId)}
        />
      ) : null}
    </SidebarMenuContainer>
  );
}
